import styled, { css } from 'styled-components';
import { Button, IconButton } from 'src/components';
import { colorSchemePreserveAttrs } from 'src/utils/colorScheme';

export const Container = styled.div(
  ({ theme }) => css`
    min-height: ${theme.sz.s48};
    background-color: ${theme.co.neutral95};
    border-radius: ${theme.bo.medium};
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: ${theme.sz.s2};
    position: relative;
    overflow: hidden;

    @media (max-width: ${theme.sz.m}) {
      border-radius: 0;
    }
  `,
);

export const CoverImage = styled.img(
  () => css`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    z-index: 0;
    pointer-events: none;
  `,
);

export const CoverChrome = styled.div.attrs(colorSchemePreserveAttrs)<{ $noOverlay?: boolean }>(
  ({ theme, $noOverlay }) => css`
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: ${theme.sz.s2};
    padding: ${theme.sz.s2} ${theme.sz.s4};

    ${
      !$noOverlay &&
      css`
        &::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(0deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.5) 100%);
          z-index: -1;
        }
      `
    }
  `,
);

export const ActiveBadge = styled.div(
  ({ theme }) => css`
    ${theme.typography.button.s};
    background: ${theme.co.primary20};
    color: ${theme.co.secondary0};
    border-radius: ${theme.bo.small};
    padding: ${theme.sz.s1} ${theme.sz.s2};
  `,
);

export const ProBadge = styled.div(
  ({ theme }) => css`
    ${theme.typography.button.s};
    background: ${theme.co.primary20};
    color: ${theme.co.secondary20};
    border-radius: ${theme.bo.small};
    padding: ${theme.sz.s1} ${theme.sz.s2};
    display: flex;
    align-items: flex-start;
    gap: ${theme.sz.s1};

    svg {
      width: ${theme.sz.s6};
      height: ${theme.sz.s6};
    }

    path {
      fill: ${theme.co.secondary20};
    }
  `,
);

export const WordCount = styled.div(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    color: ${theme.co.secondary20};
    background: ${theme.co.secondary90};
    padding: ${theme.sz.s1} ${theme.sz.s2};
    border-radius: ${theme.bo.small};
  `,
);

export const BadgeContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.sz.s2};
    margin-bottom: ${theme.sz.s2};
  `,
);

export const CompletedBadge = styled.div(
  ({ theme }) => css`
    background: ${theme.co.secondary40};
    color: ${theme.co.neutral99};
    padding: ${theme.sz.s1} ${theme.sz.s2};
    border-radius: ${theme.bo.small};
    display: flex;
    align-items: center;
    gap: ${theme.sz.s1};
    ${theme.typography.button.s};

    svg {
      width: ${theme.sz.s6};
      height: ${theme.sz.s6};
    }
  `,
);

export const Title = styled.h1(
  ({ theme }) => css`
    color: ${theme.co.neutral99};
    ${theme.typography.heading.desktop.m};
    margin: 0;
  `,
);

export const DescriptionContainer = styled.div(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: ${theme.sz.s1};
    color: ${theme.co.primary20};
    cursor: pointer;
    path {
      fill: ${theme.co.primary20};
    }
  `,
);

export const LearnerCountBadge = styled.div(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    background: ${theme.co.tertiary98};
    padding: ${theme.sz.s1} ${theme.sz.s2};
    border-radius: ${theme.bo.small};
    display: flex;
    align-items: center;
    gap: ${theme.sz.s1};
    svg {
      width: ${theme.sz.s6};
      height: ${theme.sz.s6};
    }
  `,
);

export const CreatorBadge = styled.div(
  ({ theme }) => css`
    ${theme.typography.paragraph.m};
    background: ${theme.co.information98};
    padding: ${theme.sz.s1} ${theme.sz.s2};
    border-radius: ${theme.bo.small};
    display: flex;
    align-items: center;
    gap: ${theme.sz.s1};
  `,
);

export const ShareButton = styled(Button)(
  ({ theme }) => css`
    ${theme.typography.button.l};
    position: absolute;
    right: ${theme.sz.s3};
    top: ${theme.sz.s3};

    svg {
      width: ${theme.sz.s6};
      height: ${theme.sz.s6};
    }

    @media (max-width: ${theme.sz.m}) {
      right: ${theme.sz.s1};
      top: ${theme.sz.s1};
    }
  `,
);

export const EditButton = styled(IconButton)(
  ({ theme }) => css`
    position: absolute;
    right: ${theme.sz.s3};
    bottom: ${theme.sz.s3};
    width: ${theme.sz.s10};
    height: ${theme.sz.s10};
    background: ${theme.co.primary20};
    transition: transform 0.2s ease-in-out;
  `,
);

export const DeleteButton = styled(IconButton)(
  ({ theme }) => css`
    position: absolute;
    right: ${theme.sz.s16};
    bottom: ${theme.sz.s3};
    width: ${theme.sz.s10};
    height: ${theme.sz.s10};
    background: ${theme.co.primary20};
    transition: transform 0.2s ease-in-out;
  `,
);
